import { useEffect } from "react";
import {
  Box,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  type GridColDef,
  type GridComparatorFn,
} from "@mui/x-data-grid-premium";
import { PrefPersistedDataGrid } from "@pepsico/layout";
import {
  getParamValue,
  PrefToggleButtonGroup,
  updateParamValue,
  usePagePrefs,
  type AutocompleteOption,
} from "@pepsico/pref-permissions";
import { interpolatePlasma } from "d3";
import { useSearchParams } from "react-router";

import {
  TaskStatus,
  type PlotGeoResponse,
  type RowGeoResponse,
  type RowSnipResponse,
  type VarietyResponse,
} from "api/model";
import { useGetRowsGeo } from "api/rows-geo/rows-geo";
import { useGetRowSnips } from "api/snips/snips";
import { useGetVarieties } from "api/varieties/varieties";
import { aggregateRowSnipsByPlot } from "components/PlotMap/utils/plotAnalysisFill";
import { useCogOrtho } from "context/CogOrtho";
import {
  getAnalyzeCanopyForTrial,
  getAnalyzeEmergenceForTrial,
  getOrthoWorkflowKey,
  useOrthoWorkflows,
} from "context/OrthoWorkflow";
import {
  COLOR_TARGET_PREF_KEY,
  PLOT_COLOR_PREF_KEY,
  RESULTS_TABLE_VIEW_PREF_KEY,
  type PlotColorBy,
  type ResultsTableView,
} from "defs";

import { replaceWithPlotAndRowSheets } from "./analysisResultsExcel";
import AnalysisResultsToolbar from "./AnalysisResultsToolbar";

const COLOR_TARGET_OPTIONS: AutocompleteOption[] = [
  { label: "Plots", value: "plots" },
  { label: "Rows", value: "rows" },
];

const PLOT_COLOR_OPTIONS: { label: string; value: PlotColorBy }[] = [
  { label: "None", value: "none" },
  { label: "Emergence", value: "emerg" },
  { label: "Row Cover", value: "rc" },
];

const defaultRowSnips: RowSnipResponse[] = [];
const defaultRowsGeo: RowGeoResponse[] = [];
const defaultVarieties: VarietyResponse[] = [];

const PLASMA_STOP_COUNT = 12;

const PLASMA_GRADIENT = Array.from({ length: PLASMA_STOP_COUNT }, (_, index) =>
  interpolatePlasma(index / (PLASMA_STOP_COUNT - 1)),
).join(", ");

const VIEW_OPTIONS: AutocompleteOption[] = [
  { label: "Plots", value: "plots" },
  { label: "Rows", value: "rows" },
];

/** One grid line: either a plot (aggregate of its rows) or a single crop row. */
interface AnalysisRow {
  emergenceCount: number | null;
  id: string;
  plot: string;
  qtRow: number | null;
  range: number | null;
  row: number | null;
  rowCover: number | null;
  variety: string;
}

interface AnalysisResultsProps {
  orthoId: number;
  plotsGeo: readonly PlotGeoResponse[];
  siteLabel: string;
  trialId: number;
}

const getPlotLabel = (
  plot: PlotGeoResponse | undefined,
  plotId: number,
): string => {
  const plotLabel = plot?.plot_label?.trim();
  if (plotLabel != null && plotLabel !== "") {
    return plotLabel;
  }
  if (plot?.plot_number != null) {
    return String(plot.plot_number);
  }
  return `Plot ${plotId}`;
};

const getIntegerPlotLabel = (label: string): number | null => {
  const trimmed = label.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return null;
  }
  return Number(trimmed);
};

const comparePlotLabels = (left: string, right: string): number => {
  const leftInteger = getIntegerPlotLabel(left);
  const rightInteger = getIntegerPlotLabel(right);
  if (leftInteger != null && rightInteger != null) {
    return leftInteger - rightInteger;
  }
  return left.localeCompare(right);
};

const plotSortComparator: GridComparatorFn<string> = comparePlotLabels;

/** Plot order, then row order within a plot; lines without a row sort first. */
const compareAnalysisRows = (left: AnalysisRow, right: AnalysisRow): number =>
  comparePlotLabels(left.plot, right.plot) ||
  (left.row ?? Number.NEGATIVE_INFINITY) -
    (right.row ?? Number.NEGATIVE_INFINITY) ||
  0;

const formatNullableNumber = (value: number | null): string =>
  value == null ? "—" : String(value);

/** Plot-column (QT row) from a plot's stored geo crop-row span. */
const getQtRow = (minRow: number, maxRow: number): number => {
  const rowsInPlot = Math.max(maxRow - minRow + 1, 1);
  return Math.floor((minRow - 1) / rowsInPlot) + 1;
};

const toExcelFileNamePart = (value: string): string => {
  const sanitized = value
    .trim()
    .replaceAll(/[<>:"/\\|?*]+/g, "")
    .replaceAll(/\s+/g, "_");
  return sanitized === "" ? "site" : sanitized;
};

const getAnalysisExcelFileName = (
  siteLabel: string,
  flightDate: string | undefined,
): string => {
  const date =
    flightDate != null && flightDate !== ""
      ? flightDate.slice(0, 10)
      : "unknown-date";
  return `${toExcelFileNamePart(siteLabel)}_${date}_analysis`;
};

/** The plot view has one line per plot, so it has no Row column. */
const PLOT_VIEW_HIDDEN_FIELDS: ReadonlySet<string> = new Set(["row"]);

const columns: GridColDef<AnalysisRow>[] = [
  {
    field: "plot",
    headerName: "Plot",
    width: 90,
    sortComparator: plotSortComparator,
  },
  {
    field: "row",
    headerName: "Row",
    width: 60,
    type: "number",
    valueFormatter: formatNullableNumber,
  },
  {
    field: "variety",
    headerName: "Variety",
    width: 90,
  },
  {
    field: "range",
    headerName: "Range",
    width: 80,
    type: "number",
    valueFormatter: formatNullableNumber,
  },
  {
    field: "qtRow",
    headerName: "QT Row",
    width: 80,
    type: "number",
    valueFormatter: formatNullableNumber,
  },
  {
    field: "emergenceCount",
    headerName: "Emergence Count",
    width: 130,
    type: "number",
    valueFormatter: formatNullableNumber,
  },
  {
    field: "rowCover",
    headerName: "Row Cover",
    width: 110,
    type: "number",
    valueFormatter: (value: number | null) =>
      value == null ? "—" : `${value.toFixed(0)}%`,
  },
];

const plotViewColumns = columns.filter(
  (column) => !PLOT_VIEW_HIDDEN_FIELDS.has(column.field),
);

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  orthoId,
  plotsGeo,
  siteLabel,
  trialId,
}) => {
  const { orthos } = useCogOrtho();
  const { byKey } = useOrthoWorkflows();
  const [prefs] = usePagePrefs();
  const view: ResultsTableView =
    getParamValue(prefs, RESULTS_TABLE_VIEW_PREF_KEY) === "rows"
      ? "rows"
      : "plots";
  const [, setSearchParams] = useSearchParams();
  const { data: rowSnips = defaultRowSnips, isPending: isSnipsPending } =
    useGetRowSnips(
      { ortho_id: orthoId, trial_id: trialId },
      {
        query: {
          select: (res): RowSnipResponse[] =>
            res.status === 200 ? res.data : defaultRowSnips,
        },
      },
    );
  const { data: rowsGeo = defaultRowsGeo, isPending: isRowsGeoPending } =
    useGetRowsGeo(
      { trial_id: trialId },
      {
        query: {
          select: (res): RowGeoResponse[] =>
            res.status === 200 ? res.data : defaultRowsGeo,
        },
      },
    );
  const { data: varieties = defaultVarieties, isPending: isVarietiesPending } =
    useGetVarieties(
      { include_inactive: true },
      {
        query: {
          select: (res): VarietyResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const plotsById = new Map(plotsGeo.map((plot) => [plot.id, plot]));
  const varietiesById = new Map(
    varieties.map((variety) => [variety.id, variety]),
  );
  const rowsGeoById = new Map(rowsGeo.map((row) => [row.id, row]));
  const aggregatesByPlotId = aggregateRowSnipsByPlot(rowSnips);

  const getPlotColumns = (plotId: number) => {
    const plot = plotsById.get(plotId);
    return {
      plot: getPlotLabel(plot, plotId),
      qtRow: plot != null ? getQtRow(plot.min_row, plot.max_row) : null,
      range: plot?.range ?? null,
      variety: varietiesById.get(plot?.variety_id ?? -1)?.variety_name ?? "—",
    };
  };

  /**
   * Both views are built from the same row snips. `plotRows` holds one line
   * per plot (aggregated by `aggregateRowSnipsByPlot`, the same values the map
   * colors by) and `rowRows` one line per crop row. The toggle picks which one
   * the grid shows; the Excel download always writes both.
   */
  const plotRows: AnalysisRow[] = [...aggregatesByPlotId]
    .map(([plotId, aggregate]) => ({
      ...getPlotColumns(plotId),
      emergenceCount: aggregate.emergenceCount,
      id: `plot-${plotId}`,
      row: null,
      rowCover: aggregate.rowCover,
    }))
    .sort(compareAnalysisRows);
  const rowRows: AnalysisRow[] = rowSnips
    .map((snip) => ({
      ...getPlotColumns(snip.plot_id),
      emergenceCount: snip.emergence_count ?? null,
      id: `row-${snip.row_id}`,
      row: rowsGeoById.get(snip.row_id)?.row_number ?? null,
      rowCover: snip.canopy_cover ?? null,
    }))
    .sort(compareAnalysisRows);

  const isPlotView = view === "plots";

  const isPending = isSnipsPending || isRowsGeoPending || isVarietiesPending;
  const selectedOrtho = orthos.find((ortho) => ortho.id === orthoId);
  const excelFileName = getAnalysisExcelFileName(
    siteLabel,
    selectedOrtho?.flight_date,
  );

  const workflow =
    selectedOrtho == null
      ? undefined
      : byKey.get(getOrthoWorkflowKey(selectedOrtho));
  const wasRun: Record<PlotColorBy, boolean> = {
    emerg:
      getAnalyzeEmergenceForTrial(workflow, trialId).status ===
      TaskStatus.completed,
    none: true,
    rc:
      getAnalyzeCanopyForTrial(workflow, trialId).status ===
      TaskStatus.completed,
  };
  const storedPlotColor = getParamValue(prefs, PLOT_COLOR_PREF_KEY);
  const plotColor: PlotColorBy =
    storedPlotColor === "emerg" || storedPlotColor === "rc"
      ? storedPlotColor
      : "none";
  const isPlotColorUnavailable = !wasRun[plotColor];

  const setPlotColor = (value: PlotColorBy) => {
    setSearchParams(
      (prev) => updateParamValue(prev, PLOT_COLOR_PREF_KEY, prefs, value),
      { replace: true, state: { inPageNav: true } },
    );
  };

  // Drop a color metric the newly selected flight never ran.
  useEffect(() => {
    if (isPlotColorUnavailable) {
      setSearchParams(
        (prev) => updateParamValue(prev, PLOT_COLOR_PREF_KEY, prefs, "none"),
        { replace: true, state: { inPageNav: true } },
      );
    }
  }, [isPlotColorUnavailable, prefs, setSearchParams]);

  return (
    <Stack spacing={2}>
      <Divider />
      <Stack spacing={0.5}>
        <Typography component="h3" variant="subtitle2">
          Color by
        </Typography>
        <PrefToggleButtonGroup
          aria-label="Color target"
          exclusive
          fullWidth
          options={COLOR_TARGET_OPTIONS}
          param={COLOR_TARGET_PREF_KEY}
          size="small"
        />
        <ToggleButtonGroup
          aria-label="Color metric"
          color="primary"
          exclusive
          fullWidth
          onChange={(_, value: PlotColorBy | null) => {
            if (value != null) {
              setPlotColor(value);
            }
          }}
          size="small"
          value={isPlotColorUnavailable ? "none" : plotColor}
        >
          {PLOT_COLOR_OPTIONS.map(({ label, value }) => (
            <Tooltip key={value} title={wasRun[value] ? "" : "Not run"}>
              <span style={{ display: "flex", flex: 1 }}>
                <ToggleButton
                  aria-label={label}
                  disabled={!wasRun[value]}
                  fullWidth
                  value={value}
                >
                  {label}
                </ToggleButton>
              </span>
            </Tooltip>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack spacing={0.5}>
        <Typography component="h3" variant="subtitle2">
          Results
        </Typography>
        <PrefToggleButtonGroup
          aria-label="Results table level"
          exclusive
          fullWidth
          options={VIEW_OPTIONS}
          param={RESULTS_TABLE_VIEW_PREF_KEY}
          size="small"
        />
      </Stack>

      <Box sx={{ height: 320, width: "100%" }}>
        <PrefPersistedDataGrid
          // Remount per view so each keeps its own columns, sort and page.
          key={view}
          columns={isPlotView ? plotViewColumns : columns}
          density="compact"
          disableRowSelectionOnClick
          getRowId={(row: AnalysisRow) => row.id}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            sorting: {
              sortModel: [
                { field: "plot", sort: "asc" },
                ...(isPlotView ? [] : [{ field: "row", sort: "asc" as const }]),
              ],
            },
          }}
          loading={isPending}
          localeText={{
            noRowsLabel: "No analysis results for this ortho.",
          }}
          pageSizeOptions={[10, 25, 50]}
          pagination
          prefKey={
            isPlotView ? "analysis-plot-results" : "analysis-row-results"
          }
          rows={isPlotView ? plotRows : rowRows}
          showToolbar
          slotProps={{
            toolbar: {
              excelOptions: {
                // Writes both a Plots and a Rows sheet, whichever view is shown.
                exceljsPostProcess: (input) => {
                  replaceWithPlotAndRowSheets(input, { plotRows, rowRows });
                  return Promise.resolve();
                },
                fileName: excelFileName,
              },
            },
          }}
          // Using a custom toolbar to avoid having all the stock toolbar buttons
          slots={{ toolbar: AnalysisResultsToolbar }}
          sx={{ height: "100%" }}
        />
      </Box>

      <Stack spacing={0.5}>
        <Typography component="h3" variant="subtitle2">
          Scale
        </Typography>
        <Box
          aria-label="Plasma color scale from navy to yellow"
          role="img"
          sx={{
            background: `linear-gradient(to right, ${PLASMA_GRADIENT})`,
            borderRadius: 1,
            height: 16,
            width: "100%",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography color="text.secondary" variant="caption">
            Low
          </Typography>
          <Typography color="text.secondary" variant="caption">
            High
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

export default AnalysisResults;
